import { CurrencyPipe } from '@angular/common';
import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { NgRedux } from '@angular-redux/store';

import { get, isFunction, padEnd } from 'lodash';
import { NUMERICAL_ORDER_AMOUNT } from '@config/index';
import {
  DEFAULT_CURRENCY_SYMBOL_POSITION,
  DEFAULT_NUMBER_PRECISION,
} from '@config/global-constants';
import { IFormattingType } from '@shared/types';
import { IAppState } from '@app/store';
import { StateService } from '../state-service';
import { lazyInject } from '@app/containers/base/decorators';

/*
  CountryFormatService
  should includes all methods to format and display country-specific values
*/
@Injectable()
export class CountryFormatService {
  @lazyInject(StateService) public stateService: StateService;

  public symbolPosition: string = DEFAULT_CURRENCY_SYMBOL_POSITION;
  public numberPrecision: number = DEFAULT_NUMBER_PRECISION;
  public getState(): IAppState {
    return this.ngRedux.getState();
  }
  public constructor(
    public currencyPipe: CurrencyPipe,
    private ngRedux: NgRedux<IAppState>,
    public injector: Injector,
  ) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public toCurrencyString(input: number | string): string {
    // symbolPosition will be placed in ng-redux store, currently we use default value
    const symbolPosition = this.symbolPosition;
    const formattedNumber = this.applyNumberFormat(
      parseFloat(String(input)),
      {},
    );

    const currencySign = this.getCurrencySymbol();

    return symbolPosition === DEFAULT_CURRENCY_SYMBOL_POSITION
      ? `${currencySign}${formattedNumber}`
      : `${formattedNumber}${currencySign}`;
  }

  public applyNumberFormat(
    input: number,
    formattingOption: IFormattingType,
  ): string {
    const precision: number = get(
      formattingOption,
      'precision',
      this.getCurrencyPrecision(),
    );

    const decimalSeparator = !precision
      ? ''
      : get(formattingOption, 'decimalSeparator', this.getDecimalSeparator());

    const thousandsSeparator: string = get(
      formattingOption,
      'thousandsSeparator',
      this.getThousandsSeparator(),
    );

    const sign = input < 0 ? '-' : '';

    const basePart = this.formNumberBasePart(input, thousandsSeparator);
    const mantissa = padEnd(String(input).split('.')[1], precision, '0').slice(
      0,
      precision,
    );

    return sign + basePart + decimalSeparator + mantissa;
  }

  public getCurrencyCode(): any {
    return this.getPreferencesProperty(this.stateService.getCurrencyCode());
  }

  public getCurrencyPrecision(): any {
    return this.getPreferencesProperty(
      this.stateService.getCurrencyPrecision(),
    );
  }

  public getPercentagePrecision(): any {
    return this.getPreferencesProperty(DEFAULT_NUMBER_PRECISION);
  }

  public getThousandsSeparator(): any {
    return this.getPreferencesProperty(
      this.stateService.getCurrencyThousandsSeparator(),
    );
  }

  public getDecimalSeparator(): any {
    return this.getPreferencesProperty(
      this.stateService.getCurrencyDecimalSeparator(),
    );
  }

  public getCurrencySymbol(): any {
    return this.getPreferencesProperty(this.stateService.getCurrencySymbol());
  }

  public getCurrencySymbolFromCode(code: string): string {
    return this.currencyPipe.transform(0, code).replace(/[0-9.,']/g, '') || '';
  }

  public getNegatives(): any {
    return this.getPreferencesProperty(this.stateService.getNegatives());
  }

  private getPreferencesProperty(defaultValue?: any): any {
    return defaultValue;
  }

  /**
   * Returns an input number divided into groups by thousandSeparator as a string
   */
  private formNumberBasePart(
    input: number,
    thousandsSeparator: string,
  ): string {
    const segmentSize = NUMERICAL_ORDER_AMOUNT;
    const baseNumberString = String(Math.trunc(Math.abs(input)));

    const regex = RegExp(
      `\\d{1,${segmentSize}}(?=(\\d{${segmentSize}})+(?!\\d))`,
      'g',
    );

    return baseNumberString.replace(regex, `$&${thousandsSeparator}`);
  }
}
